// SM-630 (SM-621 phase 15) — the external-credit ("mini app") session, as
// the client draws it.
//
// A THIRD_PARTY_CREDIT account keeps its money at the provider. Smart
// Market never holds a wallet for it: each BUY is pre-funded by a provider
// Deduct before the order may trade, and whatever is owed back (unused
// funding, sale proceeds, a redemption payout) is returned by a provider
// Credit. This file is everything the client knows about that, and the
// rule it holds to is MINI-APP-FLOW's: the client renders the figures the
// server calculated and nothing else.
//
//	- every money figure comes from GET /api/wallet/external-credit/state
//	  (server-calculated at provider precision); the client never adds,
//	  subtracts or promotes one — a pending Credit is never "available",
//	  a funding-pending order is never "active";
//	- the account comes from the session, never from anything the client
//	  sends (the gateway stamps it; no request here names one);
//	- the browser never calls the provider and never carries a balance,
//	  amount, direction, provider user id or credential.
//
// Hiding a control here is presentation only. The server refuses GAME,
// conversion, native funding and the auto-redeem preference for this
// account regardless (SM-625, SM-648, B7).
import { api } from "./api";

// ── Wire (mirror of contracts/httpapi/v1 broker_mini_app.go) ─────────

export const EXTERNAL_CREDIT_STATE_PATH = "/api/wallet/external-credit/state";

/** A provider amount: a decimal string with exactly four fractional digits. */
export type ProviderAmount = string;

export type FundingStatus =
  | "FUNDING_PENDING"
  | "FUNDING_COMMITTED"
  | "FUNDING_NOT_REQUIRED"
  | "FUNDING_REJECTED"
  | "FUNDING_RETURNED"
  | "FUNDING_SETTLED";

export interface ExternalCreditBalance {
  broker_account_id: string;
  currency: string;
  available_to_trade: ProviderAmount;
  snapshot_balance: ProviderAmount;
  unresolved_deduct_reserve: ProviderAmount;
  other_provider_locks: ProviderAmount;
  prepaid_funding?: ProviderAmount;
  pending_credit?: ProviderAmount;
  position_value?: ProviderAmount;
  unrealized_pnl?: string;
  portfolio_equity?: string;
  snapshot_version: number;
  provider_as_of?: string;
  fetched_at?: string;
  stale: boolean;
}

export interface ExternalCreditFundingRow {
  order_id: string;
  client_order_id?: string;
  side: string;
  amount: ProviderAmount;
  status: FundingStatus;
  reject_code?: string;
  provider_code?: string;
  provider_transaction_id?: string;
  release_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface ExternalCreditCreditRow {
  receivable_id: string;
  kind: "UNUSED_FUNDING" | "TRADE_PROCEEDS" | "REDEMPTION_PAYOUT" | string;
  business_ref_kind: string;
  business_ref_id: string;
  amount: ProviderAmount;
  state: "PENDING" | "DISPATCHED" | "UNKNOWN" | "COMMITTED" | "FAILED" | string;
  pending: boolean;
  provider_transaction_id: string;
  created_at: string;
  updated_at: string;
}

export interface ExternalCreditState {
  broker_account_id: string;
  currency: string;
  balance: ExternalCreditBalance;
  funding_pending: ProviderAmount;
  funding_pending_count: number;
  funding_failed_count: number;
  credit_pending?: ProviderAmount;
  credit_pending_count: number;
  credit_committed: ProviderAmount;
  credits_unavailable?: boolean;
  committed_since: string;
  row_limit: number;
  fundings: ExternalCreditFundingRow[];
  credits: ExternalCreditCreditRow[];
  status_poll_schedule_ms: number[];
  escalate_after_ms: number;
  as_of: string;
}

/** The session's funding state. Only an external-credit session has it. */
export function fetchExternalCreditState(): Promise<ExternalCreditState> {
  return api<ExternalCreditState>(EXTERNAL_CREDIT_STATE_PATH);
}

// ── How often the state is re-read (SM-815) ──────────────────────────

/** The state's poll while nothing is settling. */
export const STATE_POLL_MS = 15_000;

/**
 * The poll while a funding or a Credit is still settling. A provider Deduct
 * and a Credit both resolve asynchronously, so the refetch right after an
 * order action can still answer "pending" — and no WS frame announces a
 * Credit (broker-service publishes none), so the poll is what carries the
 * provider's answer to the band and the chip.
 */
export const STATE_SETTLING_POLL_MS = 2_000;

/**
 * How long one funding or Credit is followed at the fast poll, from when
 * it was raised. A Credit drains in a second or two, and the server
 * re-checks an undecided Deduct on its own fast schedule for under a
 * minute (status_poll_schedule_ms), then once a minute. One still pending
 * past this is in recovery — the provider is slow or down — and reading
 * the state every 2 s would add load without showing the answer sooner.
 */
export const STATE_SETTLING_MAX_MS = 60_000;

/**
 * How long the state is followed at the fast poll after one of the
 * session's orders fills or ends. The money such a frame moves — the
 * Credit returning a cancelled order's unused funding, a sale's proceeds —
 * is raised by settlement after the frame, and until it is, nothing in the
 * state says anything is on its way.
 */
export const STATE_FOLLOW_MS = 15_000;

/**
 * Whether a funding is undecided or a Credit is on its way back, raised
 * within STATE_SETTLING_MAX_MS. Ages are the server's (the state's as_of
 * against each row's created_at), so the browser's clock plays no part.
 * The rows are the newest of each kind: a pending item beyond them is
 * older than every row shown.
 */
export function hasSettlingFunds(state: ExternalCreditState | undefined): boolean {
  if (!state) return false;
  const asOf = Date.parse(state.as_of);
  const recent = (createdAt: string) => asOf - Date.parse(createdAt) < STATE_SETTLING_MAX_MS;
  return (
    state.fundings.some((f) => f.status === "FUNDING_PENDING" && recent(f.created_at)) ||
    state.credits.some((c) => c.pending && recent(c.created_at))
  );
}

/**
 * The refetch interval for the state as last read: fast while something
 * is settling, or until `followUntil` (STATE_FOLLOW_MS after an order's
 * fill or terminal frame); otherwise the slow poll.
 */
export function statePollMs(state: ExternalCreditState | undefined, followUntil?: number, now: number = Date.now()): number {
  if (followUntil !== undefined && now < followUntil) return STATE_SETTLING_POLL_MS;
  return hasSettlingFunds(state) ? STATE_SETTLING_POLL_MS : STATE_POLL_MS;
}

/** GET /api/orders/{id}/provider-status (OrderFundingItem). */
export interface OrderProviderStatus {
  order_id: string;
  status: string;
  funding_resolution?: string;
  status_text: string;
  cancel_requested?: boolean;
}

export function fetchOrderProviderStatus(orderId: string): Promise<OrderProviderStatus> {
  return api<OrderProviderStatus>(`/api/orders/${encodeURIComponent(orderId)}/provider-status`);
}

// ── Display ──────────────────────────────────────────────────────────

/**
 * A provider amount in the currency formatters' minor units (4 dp),
 * WITHOUT arithmetic on its value: "12.3400" → "123400". An absent or
 * malformed figure is undefined, so a component draws its placeholder —
 * an unknown figure is never shown as 0.
 */
export function providerAmountToMinor(a: string | undefined | null): string | undefined {
  if (a == null) return undefined;
  const m = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(a.trim());
  if (!m) return undefined;
  const [, sign, whole, frac = ""] = m;
  const digits = (whole + frac.padEnd(4, "0")).replace(/^0+(?=\d)/, "");
  return digits === "0" ? "0" : sign + digits;
}

// ── Orders: what a funding-pending order is ──────────────────────────

/** The order status of an external-credit BUY whose Deduct is undecided. */
export const ORDER_STATUS_PENDING_FUNDING = "PENDING_FUNDING";

/**
 * SM-666: an order whose funding is pending is VISIBLE but never active —
 * it cannot fill and is not counted with the working orders. A cancel is
 * accepted and applied when the funding resolves (SM-623 §6): if the
 * Deduct then commits, the order never activated and the funding is
 * returned by Rollback. So the control reads "cancel request", not
 * "cancel order".
 */
export function isFundingPending(status: string | undefined | null): boolean {
  return status === ORDER_STATUS_PENDING_FUNDING;
}

/**
 * SM-623 §6's status-query schedule, as the server served it: wait
 * schedule[i] before the i-th check; past the end the last interval
 * repeats. A missing schedule falls back to the contract's numbers.
 */
export const DEFAULT_POLL_SCHEDULE_MS = [0, 1_000, 3_000, 10_000, 30_000, 60_000];
export const DEFAULT_ESCALATE_AFTER_MS = 15 * 60_000;

export function pollDelayMs(attempt: number, schedule: readonly number[] = DEFAULT_POLL_SCHEDULE_MS): number {
  const s = schedule.length ? schedule : DEFAULT_POLL_SCHEDULE_MS;
  if (attempt < s.length) return Math.max(0, s[attempt]);
  return Math.max(1_000, s[s.length - 1]);
}

// ── Launch outcomes (SM-654) ─────────────────────────────────────────

/** The query parameter the gateway's 303 names a refused launch with. */
export const LAUNCH_OUTCOME_QUERY = "launch";

/**
 * What the landing renders. The gateway names exactly three refusal
 * classes (contracts broker/v1 LaunchDenyClass) and deliberately merges
 * an invalid, expired and already-used code into one — telling them
 * apart would tell an attacker which codes exist. Anything else the
 * landing meets (no session after a clean redirect, an unknown class, a
 * bootstrap that fails) is the retry-safe failure: "launch again from
 * your provider app", never a button that re-sends a code.
 */
export type LaunchView =
  | "loading"
  | "success"
  | "invalid_code"
  | "inactive_account"
  | "provider_unavailable"
  | "retry_safe_failure";

export function launchViewForClass(cls: string | undefined): LaunchView | undefined {
  switch (cls) {
    case undefined:
    case "":
      return undefined;
    case "INVALID_OR_EXPIRED":
      return "invalid_code";
    case "ACCOUNT_UNAVAILABLE":
      return "inactive_account";
    case "PROVIDER_UNAVAILABLE":
      return "provider_unavailable";
  }
  return "retry_safe_failure";
}

// ── Host app (WebView) boundary ──────────────────────────────────────

/**
 * The ONLY messages the mini app sends its host (MINI-APP-FLOW "Browser
 * and Host-App Boundary"): non-financial lifecycle signals. No balance,
 * amount, account or transaction id ever crosses it.
 */
export type HostLifecycleMessage = "REQUEST_CLOSE" | "SESSION_EXPIRED";

interface HostWindow {
  ReactNativeWebView?: { postMessage(m: string): void };
  webkit?: { messageHandlers?: Record<string, { postMessage(m: unknown): void } | undefined> };
}

/**
 * Posts a lifecycle message to the hosting WebView when there is one
 * (React Native, or a WKWebView handler named "smartMarket"); a plain
 * browser has no host and nothing happens. Returns whether a host took it.
 */
export function postHostLifecycle(message: HostLifecycleMessage, w: Window & HostWindow = window as Window & HostWindow): boolean {
  const payload = JSON.stringify({ type: message });
  try {
    if (w.ReactNativeWebView) {
      w.ReactNativeWebView.postMessage(payload);
      return true;
    }
    const wk = w.webkit?.messageHandlers?.smartMarket;
    if (wk) {
      wk.postMessage(payload);
      return true;
    }
  } catch {
    // A host that throws is a host that did not take it.
  }
  return false;
}

// ── Place errors ─────────────────────────────────────────────────────

/**
 * Whether a failed place may still have created the order: no response at
 * all (a TypeError from fetch), a gateway timeout, or a 5xx. Only then may
 * the trader's retry re-send the SAME client_order_id — a definitive 4xx
 * refusal means nothing exists, and the next submit is a new order.
 */
export function isAmbiguousPlaceError(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === "number") return status >= 500 || status === 408;
  return err instanceof Error;
}
